import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { ExportMenu } from './ExportMenu';

export interface DTColumn<T> {
  key: string;
  label: string;
  value: (row: T) => string;            // used for sort / filter / search / export
  render?: (row: T) => ReactNode;       // optional custom cell (defaults to value())
}

interface Props<T> {
  exportName: string;
  columns: DTColumn<T>[];
  rows: T[];
  getId: (row: T) => string;
  loading?: boolean;
  emptyText?: string;
  /** New / Edit / Delete are optional: a caller passes `undefined` when the user's
   *  Module Access Control rights do not allow the action, and the button (or the
   *  row pencil / select column) is then HIDDEN, never greyed. */
  onNew?: () => void;
  onEdit?: (row: T) => void;
  /** Delete the CHECKED rows — wired to the toolbar Delete (gated on selection),
   *  replacing the old per-row Actions-column delete (owner request 2026-10-13). */
  onDeleteSelected?: (rows: T[]) => void | Promise<void>;
  /** Reload the list — the canonical toolbar Refresh (BO UI Standard §6). */
  onRefresh?: () => void;
}

/**
 * List body for the accounting master pages, laid out to the BO UI Standard v2
 * vertical order (the page's FormTitle + "N items" subtitle live ABOVE, on the
 * grey page, owned by the page itself):
 *   1. the shared <AppToolbar> on its OWN full-width row — ＋ New · 🔄 Refresh
 *      left-aligned, 📤 Export to pushed hard right (.app-toolbar__spacer);
 *   2. the shared .list-search-row global search;
 *   3. ONE white grid card (.acc-card) containing ONLY the shared <AppDataGrid>
 *      (per-column header filters, click-to-sort, pagination, single footer).
 * Search filters the in-memory rows; the filtered set feeds both the grid and
 * the export. A row-SELECT checkbox is the first column and a per-row Edit pencil
 * the second; Delete moved to the toolbar, acting on the checked rows (owner
 * request 2026-10-13 — replaced the old right-hand Actions column).
 */
export function DataTable<T>({
  exportName, columns, rows, getId, loading, emptyText, onNew, onEdit, onDeleteSelected, onRefresh,
}: Props<T>) {
  const [search, setSearch] = useState('');
  // Checked row ids (the shared grid's checkbox column) — drive the toolbar Delete.
  const [selIds, setSelIds] = useState<Set<string | number>>(new Set());

  const onDeleteClick = async () => {
    const sel = rows.filter((r) => selIds.has(getId(r)));
    if (sel.length === 0 || !onDeleteSelected) return;
    await onDeleteSelected(sel);
    setSelIds(new Set());
  };

  // Maximize (search-row "Zoom"): the grid's own Export + Maximize strip is moved
  // OUT of the card onto the search row (owner request 2026-10-13, Formula Library
  // parity); the grid gets hideHeaderTools so they don't appear twice. Maximize
  // fills the grid card to the viewport (toolbar + page title hidden) while the
  // search row — with Export + Restore — stays visible, mirroring ModuleListView's
  // fullgrid. The .acc-fullgrid class lives on the page (it owns the title chrome),
  // toggled here via the nearest .acc-page ancestor.
  const rootRef = useRef<HTMLDivElement>(null);
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    const page = rootRef.current?.closest('.acc-page');
    if (!page) return;
    page.classList.toggle('acc-fullgrid', gridMax);
    return () => page.classList.remove('acc-fullgrid');
  }, [gridMax]);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setGridMax(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gridMax]);

  // Global search across every column's display value (sort / per-column filter
  // / pagination are handled by AppDataGrid on the rows we hand it).
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => columns.some((c) => c.value(r).toLowerCase().includes(q)));
  }, [rows, columns, search]);

  // Map the accounting column config → shared-grid columns, then PREPEND the Edit
  // pencil as the first data column (right after the select checkbox). Delete is on
  // the toolbar now, so there is no Actions column (owner request 2026-10-13).
  const gridCols: AppColumn<T>[] = useMemo(() => {
    const mapped: AppColumn<T>[] = columns.map((c) => ({
      field: c.key,
      header: c.label,
      text: c.value,                                   // sort / filter / default cell text
      ...(c.render ? { render: c.render } : {}),
    }));
    if (onEdit) {
      mapped.unshift({
        field: '__edit',
        header: '',
        align: 'center',
        sortable: false,
        filterable: false,
        width: 60,
        render: (r) => (
          <button className="acc-row-btn" title="Edit" onClick={(e) => { e.stopPropagation(); onEdit(r); }}>
            <i className="ti ti-pencil" />
          </button>
        ),
      });
    }
    return mapped;
  }, [columns, onEdit]);

  return (
    <div className="bl-grid-area" ref={rootRef}>
      {/* Action toolbar — its OWN full-width row on the grey page (never on the
          title row). Canonical order via the shared <AppToolbar>; the Export-to
          dropdown rides the shared spacer so it sits at the far right. Hidden while
          the grid is maximized (the search row keeps Export + Restore). */}
      {!gridMax && (
        <div className="mlv-toolbar-row">
        <AppToolbar
          onNew={onNew}
          onDelete={onDeleteSelected ? onDeleteClick : undefined}
          deleteDisabled={selIds.size === 0}
          onRefresh={onRefresh}
          refreshing={loading}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename={exportName}
                headers={columns.map((c) => c.label)}
                rows={visible.map((r) => columns.map((c) => c.value(r)))} />
            </span>
          }
        />
        </div>
      )}
      {/* Search lives in its OWN row BELOW the toolbar (standard) — never inside the
          card. Export + Maximize icons sit at the RIGHT end of this row (Formula
          Library parity), NOT inside the grid (owner request 2026-10-13). */}
      <div className="list-search-row">
        <div className="list-search">
          <span className="tb-i">🔍</span>
          <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
        </div>
        <div className="mlv-grid-acts">
          <ExportMenu iconOnly triggerClassName="adg-max-btn" filename={exportName}
            headers={columns.map((c) => c.label)}
            rows={visible.map((r) => columns.map((c) => c.value(r)))} />
          <button className={`adg-max-btn${gridMax ? ' adg-on' : ''}`} onClick={() => setGridMax((v) => !v)}
            title={gridMax ? 'Restore' : 'Maximize'} aria-label={gridMax ? 'Restore' : 'Maximize'}>
            <i className={`ti ti-arrows-${gridMax ? 'minimize' : 'maximize'}`} />
          </button>
        </div>
      </div>
      {/* ONE white grid card — grid + its single footer only. */}
      <div className="bl-card bl-card-flex">
        <AppDataGrid<T>
          rows={visible}
          columns={gridCols}
          getRowId={(r) => getId(r)}
          loading={loading}
          emptyText={emptyText}
          hideHeaderTools
          selectable={!!onDeleteSelected}
          selectedIds={selIds}
          onSelectionChange={setSelIds}
        />
      </div>
    </div>
  );
}
